import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import { axiosInstance, ENDPOINT } from 'common/constants';
import { handleResponse } from 'common/helpers';
import Sparkline from 'common/components/atoms/Sparkline';

const CRITICAL = '#d92b2b';
const WARNING = '#e8890c';
const INK_2 = '#6b6e66';
const INK_3 = '#999b95';
const LINE = '#e5e8e1';
const BG = '#f6f7f4';
const BLUE = '#2f6fd0';

const TITLE_COLOR = '#7c8174';

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${TITLE_COLOR};
  margin-bottom: 8px;
`;

const FullLink = styled(Link)`
  margin-left: auto;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0;
  color: ${BLUE};
  text-decoration: none;

  &:hover { text-decoration: underline; }
`;

const Spark = styled.div`
  border: 1px solid ${LINE};
  border-radius: 8px;
  padding: 8px 10px 4px;
  background: white;
`;

const SparkLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  color: ${INK_2};
  margin-bottom: 4px;

  b { color: ${INK_3}; font-weight: 700; }
`;

const DirTok = styled.span`
  display: inline-block;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${CRITICAL};
  background: #fcecec;
  border-radius: 4px;
  padding: 0 5px;
  margin-left: 4px;
`;

const Caption = styled.div`
  font-size: 10.5px;
  color: ${INK_3};
  margin-top: 6px;
  line-height: 1.4;

  b { color: ${INK_2}; }
`;

const Loading = styled.div`
  height: 54px;
  border-radius: 6px;
  background: ${BG};
`;

const NoTrend = styled.div`
  font-size: 11.5px;
  color: ${INK_3};
  line-height: 1.45;
  border: 1px dashed ${LINE};
  border-radius: 8px;
  padding: 12px 12px;

  b { color: ${INK_2}; }
`;

const primaryReason = (item) => {
  const reason = item.reason_for_attention || {};
  const alarmMeta = (item.ara_metadata
    && item.ara_metadata.alarms
    && item.ara_metadata.alarms[0]) || null;
  if (reason.alarms && reason.alarms.length) {
    return { kind: 'alarm', alarmMeta };
  }
  if (reason.health_score && reason.health_score.length) {
    return { kind: 'health' };
  }
  return { kind: 'none' };
};

class ContextualTrend extends React.Component {
  reqId = 0;

  state = { loading: false, series: null };

  componentDidMount() {
    this.fetchTrend();
  }

  componentDidUpdate(prevProps) {
    const prevId = prevProps.item && prevProps.item.id;
    const id = this.props.item && this.props.item.id;
    if (id !== prevId) this.fetchTrend();
  }

  componentWillUnmount() {
    this.reqId += 1;
  }

  fetchTrend = () => {
    const { item } = this.props;
    if (!item) return;
    const token = ++this.reqId;
    const { kind, alarmMeta } = primaryReason(item);

    if (kind === 'alarm') {
      if (!alarmMeta || !alarmMeta.tag_id) {
        if (token === this.reqId) this.setState({ loading: false, series: null });
        return;
      }
      this.setState({ loading: true, series: null });
      const params = { days: 30 };
      if (alarmMeta.feature) params.feature = alarmMeta.feature;
      if (alarmMeta.amp_type) params.amp_type = alarmMeta.amp_type;
      handleResponse(axiosInstance.get(ENDPOINT.GET_TAG_TREND(alarmMeta.tag_id), { params }))
        .then(
          (res) => {
            if (token !== this.reqId) return;
            const series = res && res.trend_data ? res.trend_data.data : null;
            this.setState({ loading: false, series });
          },
          () => { if (token === this.reqId) this.setState({ loading: false, series: null }); }
        );
      return;
    }

    if (kind === 'health') {
      this.setState({ loading: true, series: null });
      handleResponse(axiosInstance.get(ENDPOINT.GET_MACHINE_HEALTH_TREND(item.id), { params: { days: 90 } }))
        .then(
          (res) => {
            if (token !== this.reqId) return;
            const series = res && res.health_trend ? res.health_trend.health_score : null;
            this.setState({ loading: false, series });
          },
          () => { if (token === this.reqId) this.setState({ loading: false, series: null }); }
        );
      return;
    }

    if (token === this.reqId) this.setState({ loading: false, series: null });
  };

  render() {
    const { item, t } = this.props;
    const { loading, series } = this.state;
    if (!item) return null;
    const { kind, alarmMeta } = primaryReason(item);

    if (kind === 'none') {
      return (
        <div>
          <SectionTitle>{t('assets.detail.trend.title', { defaultValue: 'Trend' })}</SectionTitle>
          <NoTrend>
            {t('assets.detail.trend.noTrend', {
              defaultValue: 'No measurement trend to show — this asset needs configuration / action, not analysis.'
            })}
          </NoTrend>
        </div>
      );
    }

    const points = (series || []).map(Number).filter(v => !Number.isNaN(v));
    const hasData = points.length >= 2;

    if (kind === 'alarm') {
      const meta = alarmMeta || {};
      const loc = meta.measurement_location || meta.component_name || '';
      const feature = meta.feature;
      const direction = meta.direction;
      const hasChannel = Boolean(meta.tag_id || loc);
      return (
        <div>
          <SectionTitle>
            {t('assets.detail.trend.alarmTitle', { defaultValue: 'Alarmed channel · trend' })}
            <FullLink to={`/machines/${item.id}/charts`}>
              {t('assets.detail.trend.openFull', { defaultValue: 'Open full chart →' })}
            </FullLink>
          </SectionTitle>
          {hasChannel ? (
            <React.Fragment>
              <Spark>
                <SparkLabel>
                  <span>
                    {loc}
                    {feature ? ` · ${feature}` : ''}
                    {direction ? <DirTok>{direction}</DirTok> : null}
                  </span>
                  <b>{t('assets.detail.trend.days30', { defaultValue: '30 days' })}</b>
                </SparkLabel>
                {loading && <Loading />}
                {!loading && hasData && <Sparkline data={points} tone="critical" height={54} />}
                {!loading && !hasData && (
                  <Caption>{t('assets.detail.trend.noData', { defaultValue: 'No recent measurements for this channel.' })}</Caption>
                )}
              </Spark>
              {direction && (
                <Caption>
                  {t('assets.detail.trend.alarmCaption', {
                    defaultValue: 'Showing the {{direction}} channel at {{loc}} — the one with the open alarm.',
                    direction: String(direction).toLowerCase(),
                    loc
                  })}
                </Caption>
              )}
            </React.Fragment>
          ) : (
            <NoTrend>
              {t('assets.detail.trend.alarmNoTag', {
                defaultValue: 'An alarm is open on this asset. Open the full chart to review the alarmed measurement.'
              })}
            </NoTrend>
          )}
        </div>
      );
    }

    // health
    return (
      <div>
        <SectionTitle>
          {t('assets.detail.trend.healthTitle', { defaultValue: 'Machine health score · trend' })}
          <FullLink to={`/machines/${item.id}/health`}>
            {t('assets.detail.trend.openFull', { defaultValue: 'Open full chart →' })}
          </FullLink>
        </SectionTitle>
        <Spark>
          <SparkLabel>
            <span>{t('assets.detail.trend.healthLabel', { defaultValue: 'Health score (0–10)' })}</span>
            <b>{t('assets.detail.trend.days90', { defaultValue: '90 days' })}</b>
          </SparkLabel>
          {loading && <Loading />}
          {!loading && hasData && (
            <Sparkline
              data={points}
              tone="neutral"
              height={54}
              domain={[0, 10]}
              thresholds={[{ value: 7, color: WARNING }, { value: 4, color: CRITICAL }]}
            />
          )}
          {!loading && !hasData && (
            <Caption>{t('assets.detail.trend.noData', { defaultValue: 'No recent health-score history.' })}</Caption>
          )}
        </Spark>
        <Caption>
          {t('assets.detail.trend.healthCaption', {
            defaultValue: 'Per-machine health score — the only meaningful machine-level series.'
          })}
        </Caption>
      </div>
    );
  }
}

ContextualTrend.propTypes = {
  item: PropTypes.object,
  t: PropTypes.func.isRequired
};

ContextualTrend.defaultProps = {
  item: null
};

export default withTranslation()(ContextualTrend);
